// components/dashboard/widgets/VolumeDefesas.jsx
import React from 'react';
import PropTypes from 'prop-types';
import { Spin, Empty } from 'antd';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line
} from 'recharts';
import { useDashboardData } from '../../../hooks/useDashboardData';

const VolumeDefesas = ({ endpoint, filtros, tipo = 'bar' }) => {
  const { dados, loading, error } = useDashboardData(endpoint, filtros);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 40 }}>
        <Spin size="large" />
        <p style={{ marginTop: 16, color: '#666' }}>Carregando volume de defesas...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        padding: '20px', 
        textAlign: 'center', 
        backgroundColor: '#fff2f0',
        border: '1px solid #ffccc7',
        borderRadius: '6px',
        color: '#a8071a'
      }}>
        <p style={{ margin: '0 0 4px 0', fontWeight: 'bold' }}>
          Erro ao carregar volume de defesas
        </p>
        <small style={{ color: '#cf1322' }}>{error}</small>
      </div>
    );
  }

  if (!dados?.length) {
    return <Empty description="Nenhum dado de volume encontrado" />;
  }

  // Formatar dados para o gráfico
  const dadosFormatados = dados.map(item => {
    const mesAno = `${String(item.mes).padStart(2, '0')}/${item.ano}`;
    const mesNome = new Date(item.ano, item.mes - 1).toLocaleDateString('pt-BR', { 
      month: 'short',
      year: '2-digit'
    });

    return {
      ...item,
      periodo: mesAno,
      mes_nome: mesNome,
      curso_abrev: item.curso ? item.curso.substring(0, 15) + (item.curso.length > 15 ? '...' : '') : 'N/A'
    };
  });

  // Agrupar por período se necessário
  const dadosAgrupados = dadosFormatados.reduce((acc, item) => {
    const key = item.periodo;
    if (!acc[key]) {
      acc[key] = {
        periodo: key,
        mes_nome: item.mes_nome,
        total_defesas: 0,
        cursos: []
      };
    }
    
    acc[key].total_defesas += item.total_defesas;
    acc[key].cursos.push({
      curso: item.curso,
      total: item.total_defesas
    });
    
    return acc;
  }, {});

  const dadosFinais = Object.values(dadosAgrupados).sort((a, b) => {
    const [mesA, anoA] = a.periodo.split('/').map(Number);
    const [mesB, anoB] = b.periodo.split('/').map(Number);
    
    if (anoA !== anoB) return anoA - anoB;
    return mesA - mesB;
  });

  // Estatísticas
  const totalDefesas = dadosFinais.reduce((sum, item) => sum + item.total_defesas, 0);
  const mediaMensal = totalDefesas / dadosFinais.length;
  const maiorVolume = Math.max(...dadosFinais.map(item => item.total_defesas));

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div style={{
          backgroundColor: '#fff',
          padding: '12px',
          border: '1px solid #d9d9d9',
          borderRadius: '6px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          minWidth: '200px'
        }}>
          <p style={{ margin: 0, fontWeight: 'bold', marginBottom: 8 }}>
            📅 {label}
          </p>
          <p style={{ margin: '4px 0', color: '#1890ff' }}>
            📊 Total de Defesas: <strong>{data.total_defesas}</strong>
          </p>
          
          {data.cursos && data.cursos.length > 0 && (
            <div style={{ marginTop: 8, fontSize: 12 }}>
              <strong>Por Curso:</strong>
              {data.cursos.slice(0, 3).map((curso, idx) => (
                <div key={idx} style={{ 
                  margin: '2px 0', 
                  color: '#666',
                  paddingLeft: 8
                }}>
                  • {curso.curso}: {curso.total}
                </div>
              ))}
              {data.cursos.length > 3 && (
                <div style={{ color: '#999', fontSize: 11 }}>
                  e mais {data.cursos.length - 3} cursos...
                </div>
              )}
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  // PropTypes para CustomTooltip
  CustomTooltip.propTypes = {
    active: PropTypes.bool,
    payload: PropTypes.arrayOf(PropTypes.shape({
      payload: PropTypes.object
    })),
    label: PropTypes.string
  };

  const renderGrafico = () => {
    if (tipo === 'line') {
      return (
        <LineChart data={dadosFinais}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis 
            dataKey="mes_nome"
            tick={{ fontSize: 12 }}
            stroke="#666"
            angle={-45}
            textAnchor="end"
            height={60}
          />
          <YAxis 
            tick={{ fontSize: 12 }}
            stroke="#666"
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          <Line
            type="monotone"
            dataKey="total_defesas"
            stroke="#1890ff"
            strokeWidth={3}
            dot={{ fill: '#1890ff', strokeWidth: 2, r: 5 }}
            activeDot={{ r: 7, stroke: '#1890ff', strokeWidth: 2 }}
            name="Volume de Defesas"
          />
        </LineChart>
      );
    }

    return (
      <BarChart data={dadosFinais}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
        <XAxis 
          dataKey="mes_nome"
          tick={{ fontSize: 12 }}
          stroke="#666"
          angle={-45}
          textAnchor="end"
          height={60}
        />
        <YAxis 
          tick={{ fontSize: 12 }}
          stroke="#666"
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend />
        <Bar
          dataKey="total_defesas"
          fill="#1890ff"
          name="Volume de Defesas"
          radius={[4, 4, 0, 0]}
        />
      </BarChart>
    );
  };

  return (
    <div style={{ height: 350 }}>
      {/* Estatísticas no topo */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-around',
        marginBottom: 16,
        padding: '8px',
        backgroundColor: '#fafafa',
        borderRadius: '4px',
        fontSize: 12
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontWeight: 'bold', color: '#1890ff' }}>{totalDefesas}</div>
          <div style={{ color: '#666' }}>Total</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontWeight: 'bold', color: '#52c41a' }}>{mediaMensal.toFixed(1)}</div>
          <div style={{ color: '#666' }}>Média/Mês</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontWeight: 'bold', color: '#fa8c16' }}>{maiorVolume}</div>
          <div style={{ color: '#666' }}>Pico</div>
        </div>
      </div>

      <ResponsiveContainer>
        {renderGrafico()}
      </ResponsiveContainer>

      {/* Resumo por período */}
      <div style={{ 
        marginTop: 8,
        fontSize: 11,
        color: '#666',
        textAlign: 'center'
      }}>
        Período: {dadosFinais.length} meses analisados 
        {dadosFinais.length > 0 && ` (${dadosFinais[0].mes_nome} a ${dadosFinais[dadosFinais.length - 1].mes_nome})`}
      </div>
    </div>
  );
};

// PropTypes
VolumeDefesas.displayName = 'VolumeDefesas';
VolumeDefesas.propTypes = {
  endpoint: PropTypes.string.isRequired,
  filtros: PropTypes.object,
  tipo: PropTypes.oneOf(['bar', 'line'])
};

VolumeDefesas.defaultProps = {
  filtros: {},
  tipo: 'bar'
};

export default VolumeDefesas;